import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./AdminMessageMonitor.css";
import { 
  FaSearch, FaExchangeAlt, FaCalendarAlt, FaHistory, 
  FaUserShield, FaDownload, FaSyncAlt 
} from "react-icons/fa";
import { Link } from "react-router-dom";

const msgApiKey = process.env.REACT_APP_API_KEY_AUTH_MSG;
const branchApiKey = process.env.REACT_APP_API_KEY_AUTH_BRANCH;

const MESSAGES_API_URL = `https://${msgApiKey}.execute-api.ap-south-1.amazonaws.com/default/branchMessage`;
const BRANCH_API_URL = `https://${branchApiKey}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;

export default function AdminMessageMonitor() {
  const [allMessages, setAllMessages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [branchA, setBranchA] = useState("");
  const [branchB, setBranchB] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [msgRes, branchRes] = await Promise.all([
        fetch(MESSAGES_API_URL),
        fetch(BRANCH_API_URL)
      ]);
      
      const msgData = await msgRes.json();
      const branchData = await branchRes.json();
      
      setAllMessages(msgData.messages || []);
      setBranches(branchData.branches || []);
    } catch (err) {
      console.error("Monitor Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filtered Chat Logic
  const filteredChat = useMemo(() => {
    if (!branchA || !branchB) return [];

    return allMessages.filter(msg => {
      const msgDate = new Date(msg.timestamp).toISOString().split('T')[0];
      const isInDateRange = msgDate >= startDate && msgDate <= endDate;
      
      const isAtoB = msg.senderName === branchA && msg.receiverName === branchB;
      const isBtoA = msg.senderName === branchB && msg.receiverName === branchA;
      
      return isInDateRange && (isAtoB || isBtoA);
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [allMessages, branchA, branchB, startDate, endDate]);

  const handleExport = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(filteredChat));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href",     dataStr);
    downloadAnchorNode.setAttribute("download", `chat_${branchA}_${branchB}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  return (
    <div className="monitor-container">
      <header className="monitor-header">
        <div className="title-section">
          <h1><FaUserShield /> Audit: Message Monitor</h1>
          <p>Official monitoring of branch-to-branch communication</p>
        </div>
        <div className="header-nav">
          <Link to="/" className="Branch-nav">Back To Admin</Link>
          <button onClick={fetchData} className="refresh-btn"><FaSyncAlt className={loading ? "spin" : ""} /></button>
        </div>
      </header>

      <main className="monitor-layout">
        {/* Left Side: Controls */}
        <aside className="monitor-sidebar">
          <div className="filter-card">
            <h3><FaSearch /> Select Branches</h3>
            <div className="input-group">
              <label>Branch 1</label>
              <select value={branchA} onChange={(e) => setBranchA(e.target.value)}>
                <option value="">-- Choose Branch --</option>
                {branches.map(b => <option key={b.email} value={b.branchName}>{b.branchName}</option>)}
              </select>
            </div>

            <div className="divider-icon"><FaExchangeAlt /></div>

            <div className="input-group">
              <label>Branch 2</label>
              <select value={branchB} onChange={(e) => setBranchB(e.target.value)}>
                <option value="">-- Choose Branch --</option>
                {branches.map(b => <option key={b.email} value={b.branchName}>{b.branchName}</option>)}
              </select>
            </div>
          </div>

          <div className="filter-card">
            <h3><FaCalendarAlt /> Date Range</h3>
            <div className="input-group">
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="input-group">
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          {filteredChat.length > 0 && (
            <button className="export-btn" onClick={handleExport}><FaDownload /> Export Chat Logs</button>
          )}
        </aside>

        {/* Right Side: Chat Viewer */}
        <section className="monitor-viewer">
          {loading ? (
            <div className="monitor-state">
              <FaSyncAlt className="spin-large" />
              <p>Fetching Encrypted Logs...</p>
            </div>
          ) : branchA && branchB ? (
            <div className="chat-log-window">
              <div className="log-header">
                Showing {filteredChat.length} messages between <strong>{branchA}</strong> and <strong>{branchB}</strong>
              </div>
              
              <div className="log-body">
                {filteredChat.length > 0 ? (
                  filteredChat.map((msg, i) => (
                    <div key={i} className={`log-entry ${msg.senderName === branchA ? 'left-entry' : 'right-entry'}`}>
                      <div className="entry-meta">
                        <span className="sender-tag">{msg.senderName}</span>
                        <span className="time-tag">{new Date(msg.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="entry-content">
                        {msg.text && <p className="text">{msg.text}</p>}
                        {msg.imageUrl && <span className="media-tag">[📷 Image Attachment]</span>}
                        {msg.audioUrl && <span className="media-tag">[🎤 Voice Message]</span>}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-chat">
                    <FaHistory size={40} />
                    <p>No messages found for this selection and date range.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="monitor-state">
              <FaUserShield size={60} />
              <p>Select two branches to begin monitoring.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}